import * as React from 'react'
import classNames from 'classnames'
import styles from './Timeline.module.scss'

export enum TimelineOrientation {
  Vertical = 'vertical',
  Horizontal = 'horizontal'
}

interface TimelineProps {
  barColor?: string
  progressColor?: string
  stepLabelColor?: string
  currentStepLabelColor?: string
  barWidth?: number
  stepSize?: number
  currentStep?: number
  progressToNextStep?: number
  steps: string[]
  rtl?: boolean
  orientation?: TimelineOrientation
  verticalBarHeight?: number
  animateCurrentStep?(): boolean
}

class Timeline extends React.Component<TimelineProps> {
  style: CSSStyleDeclaration = document.documentElement.style

  componentDidMount() {
    const {
      barColor = 'lightGrey',
      barWidth = 2,
      stepSize = 8,
      stepLabelColor = '#b4b4b4',
      currentStepLabelColor = '#000000',
      verticalBarHeight = 100,
      steps,
    } = this.props
    const barHeight = Math.max(25 * steps.length, verticalBarHeight)
    this.style.setProperty('--bar-color', barColor)
    this.style.setProperty('--step-label-color', stepLabelColor)
    this.style.setProperty('--current-step-label-color', currentStepLabelColor)
    this.style.setProperty('--bar-width', `${barWidth}px`)
    this.style.setProperty('--step-size', `${stepSize}px`)
    this.style.setProperty('--vertical-bar-height', `${barHeight}px`)
  }

  render() {
    const {
      currentStep = 0,
      progressToNextStep = 0,
      steps,
      rtl = false,
      orientation = TimelineOrientation.Vertical,
      progressColor = 'green',
      animateCurrentStep = () => true,
    } = this.props
    this.style.setProperty('--progress-color', progressColor)
    this.style.setProperty('--current-step-shadow-color', `${progressColor}44`)

    if (!steps || steps.length < 2) {
      return <div className={classNames(styles.circle, styles.finishedStep)} />
    }

    const numOfSteps = steps.length - 1
    const eachStepValue = 100.0 / numOfSteps
    const normalizedProgressToNextStep = Math.max(0.0, Math.min(100.0, progressToNextStep))
    const progressToNextStepFactor = 100.0 / normalizedProgressToNextStep
    const progress = (currentStep * eachStepValue) + (eachStepValue / progressToNextStepFactor)
    const displayedProgress = rtl || orientation === TimelineOrientation.Vertical ? 100 - progress : progress
    this.style.setProperty('--bar-progress', `${displayedProgress}%`)
    const circlesContainerClasses = {
      [styles.circlesContainer]: true,
      [styles.rtl]: rtl,
      [styles.vertical]: orientation === TimelineOrientation.Vertical,
    }
    const barClasses = {
      [styles.bar]: true,
      [styles.vertical]: orientation === TimelineOrientation.Vertical,
    }

    const circles = []
    for (let i = 0; i < steps.length; i++) {
      const stepClasses = {
        [styles.circle]: true,
        [styles.finishedStep]: i < currentStep,
        [styles.currentStep]: i === currentStep,
        [styles.animate]: animateCurrentStep() && i === currentStep,
        [styles.vertical]: orientation === TimelineOrientation.Vertical,
        [styles.firstStep]: orientation !== TimelineOrientation.Vertical && i === 0,
        [styles.lastStep]: orientation !== TimelineOrientation.Vertical && i === steps.length - 1,
      }
      const labelClasses = {
        [styles.stepLabel]: true,
        [styles.currentStep]: i === currentStep,
        [styles.vertical]: orientation === TimelineOrientation.Vertical,
      }

      circles.push(
        <span
          className={classNames(stepClasses)}
          key={`step_circle_${i}`}>
          <div
            className={classNames(labelClasses)}
            key={`step_label_${i}`}>
            {steps[i]}
          </div>
        </span>,
      )
    }
    return (
      <div>
        <div className={classNames(circlesContainerClasses)}>
          <div className={classNames(barClasses)}>
            {circles}
          </div>
        </div>
      </div>
    )
  }
}

export default Timeline
